import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BatteryCharging, Car, Clock, Gauge, Pause, Play, PlugZap, Search, Thermometer, Timer, Zap } from "lucide-react";
import useLiveChargingStatus from "../hooks/useLiveChargingStatus";
import { SegmentedControl, Skeleton } from "../components/Analytics/AnalyticsUI";
import { formatDecimal, formatDuration, formatNumber } from "../utils/analyticsMetrics";

// A meter reading older than this is shown as stale.
const STALE_AFTER_MS = 90 * 1000;

// legacy ESP gun status 3 = charger unavailable
const statusOf = (gun) => {
  if (gun.state === "charging") return "charging";
  if (gun.state === "postcharging") return "finishing";
  if (gun.state === "connected") return "connected";
  if (gun.legacy_status === 3) return "unavailable";
  return "available";
};

const STATUS = {
  charging: { label: "Charging", text: "text-green-700", dot: "bg-green-500", ring: "#22c55e", pulse: true },
  finishing: { label: "Finishing", text: "text-amber-700", dot: "bg-amber-500", ring: "#f59e0b" },
  connected: { label: "Vehicle connected", text: "text-brand-700", dot: "bg-brand-500", ring: "#6366f1" },
  available: { label: "Available", text: "text-neutral-600", dot: "bg-neutral-400" },
  unavailable: { label: "Unavailable", text: "text-red-700", dot: "bg-red-500" },
};

const RANK = ["charging", "finishing", "connected", "unavailable", "available"];
const isActive = (status) => status === "charging" || status === "finishing";

// watts currently delivered by a gun (only while charging) and by a whole station
const gunPower = (gun) => (statusOf(gun) === "charging" ? Number(gun.power_w) || 0 : 0);
const stationPower = (station) => station.guns.reduce((a, g) => a + gunPower(g), 0);

const FILTERS = [
  { value: "all", label: "All" },
  { value: "charging", label: "Charging" },
  { value: "connected", label: "Connected" },
  { value: "available", label: "Available" },
  { value: "unavailable", label: "Unavailable" },
];

const matchesFilter = (status, filter) =>
  filter === "all" || (filter === "charging" ? isActive(status) : status === filter);

const gunLabel = (gun) =>
  gun.gun_id === "yin" ? "Gun 1 · Yin" : gun.gun_id === "yang" ? "Gun 2 · Yang" : `Gun ${gun.connector_id}`;

const kw = (w) => (w == null ? null : w / 1000);
const kwh = (wh) => (wh == null ? null : wh / 1000);
const dec = (v) => (v == null ? null : formatDecimal(v));

const relative = (date, now) => {
  if (!date) return "";
  const s = Math.max(0, Math.round((now - date) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  return `${Math.round(s / 60)}m ago`;
};

const Dash = () => <span className="text-neutral-300">—</span>;

const StatusDot = ({ status, size = "h-2 w-2" }) => {
  const s = STATUS[status];
  return (
    <span className={`relative flex ${size}`}>
      {s.pulse && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${s.dot} opacity-60`} />}
      <span className={`relative inline-flex ${size} rounded-full ${s.dot}`} />
    </span>
  );
};

const StatusLabel = ({ status }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${STATUS[status].text}`}>
    <StatusDot status={status} />
    {STATUS[status].label}
  </span>
);

// circular state-of-charge gauge; "—" when the vehicle doesn't report it
const SocRing = ({ soc, status }) => {
  const r = 40;
  const c = 2 * Math.PI * r;
  const pct = soc == null ? 0 : Math.max(0, Math.min(100, soc));
  return (
    <div className="relative h-[104px] w-[104px] flex-shrink-0">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="currentColor" strokeWidth="7" className="text-neutral-100" />
        {soc != null && (
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={STATUS[status].ring}
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c * (1 - pct / 100)}
            className="transition-[stroke-dashoffset] duration-700"
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {soc == null ? (
          <span className="text-lg font-semibold text-neutral-300">—</span>
        ) : (
          <span className="text-2xl font-semibold leading-none tracking-tight tabular-nums text-brand-950">
            {formatNumber(pct)}
            <span className="text-sm font-medium text-neutral-400">%</span>
          </span>
        )}
        <span className="mt-1 text-[10px] font-medium uppercase tracking-wider text-neutral-400">Battery</span>
      </div>
    </div>
  );
};

const KeyStat = ({ icon: Icon, label, value, unit }) => (
  <div className="min-w-0">
    <p className="flex items-center gap-1 text-[11px] font-medium text-neutral-500">
      <Icon className="h-3 w-3 text-neutral-400" />
      {label}
    </p>
    <p className="mt-0.5 truncate text-base font-semibold tabular-nums text-brand-950">
      {value == null ? <Dash /> : value}
      {value != null && unit && <span className="ml-0.5 text-xs font-medium text-neutral-400">{unit}</span>}
    </p>
  </div>
);

const Telemetry = ({ icon: Icon, label, value, unit }) => (
  <div className="flex min-w-0 flex-col items-center px-2 py-2">
    <span className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-neutral-400">
      <Icon className="h-3 w-3" />
      {label}
    </span>
    <span className="mt-0.5 text-13 font-semibold tabular-nums text-neutral-800">
      {value == null ? <Dash /> : value}
      {value != null && <span className="ml-0.5 text-[11px] font-medium text-neutral-400">{unit}</span>}
    </span>
  </div>
);

const VehicleTag = ({ id }) =>
  id ? (
    <span className="inline-flex max-w-full items-center gap-1 truncate rounded-md bg-neutral-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-neutral-700">
      <Car className="h-3 w-3 flex-shrink-0 text-neutral-400" />
      {id}
    </span>
  ) : null;

// a gun with a session (charging, finishing, or a vehicle plugged in)
const SessionGun = ({ gun, status, now }) => {
  const active = isActive(status);
  const readingAt = gun.reading_at ? new Date(gun.reading_at) : null;
  const stale = active && readingAt && now - readingAt > STALE_AFTER_MS;
  const elapsed = gun.session_start ? now / 1000 - gun.session_start : null;
  const eta =
    status === "charging" ? (gun.time_to_full_seconds ? formatDuration(gun.time_to_full_seconds) : null) : null;

  return (
    <div
      className={`rounded-xl border bg-white p-4 ${
        status === "charging" ? "border-green-200/80 shadow-[0_1px_0_0_rgb(34_197_94/0.08)]" : "border-neutral-200"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-13 font-semibold text-brand-950">{gunLabel(gun)}</span>
        <StatusLabel status={status} />
      </div>
      {gun.vehicle_id && (
        <div className="mt-2">
          <VehicleTag id={gun.vehicle_id} />
        </div>
      )}

      <div className="mt-3 flex items-center gap-4">
        <SocRing soc={gun.soc_pct} status={status} />
        {active ? (
          <div className="grid min-w-0 flex-1 grid-cols-2 gap-x-3 gap-y-3">
            <KeyStat icon={Timer} label={status === "finishing" ? "Status" : "Full in"} value={status === "finishing" ? "Finishing" : eta} />
            <KeyStat icon={Zap} label="Power" value={dec(kw(gun.power_w))} unit="kW" />
            <KeyStat icon={BatteryCharging} label="Energy" value={dec(kwh(gun.energy_wh))} unit="kWh" />
            <KeyStat icon={Clock} label="Elapsed" value={elapsed > 0 ? formatDuration(elapsed) : null} />
          </div>
        ) : (
          <p className="min-w-0 flex-1 text-13 text-neutral-500">Vehicle plugged in, waiting for charging to start.</p>
        )}
      </div>

      {active && (
        <div className="mt-4 grid grid-cols-3 divide-x divide-neutral-200/80 rounded-lg bg-neutral-50 ring-1 ring-inset ring-neutral-200/70">
          <Telemetry icon={Gauge} label="Voltage" value={dec(gun.voltage_v)} unit="V" />
          <Telemetry icon={PlugZap} label="Current" value={dec(gun.current_a)} unit="A" />
          <Telemetry icon={Thermometer} label="Temp" value={dec(gun.temperature_c)} unit="°C" />
        </div>
      )}

      {stale && (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-amber-700">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          No new meter reading for {relative(readingAt, now).replace(" ago", "")}
        </p>
      )}
    </div>
  );
};

// a gun without a session: one compact row, no telemetry
const IdleGun = ({ gun, status }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-neutral-200/80 bg-neutral-50/60 px-3 py-2.5">
    <div className="flex min-w-0 items-center gap-2.5">
      <StatusDot status={status} />
      <div className="min-w-0">
        <p className="text-13 font-medium text-brand-950">{gunLabel(gun)}</p>
        <p className="truncate text-xs text-neutral-500">
          {status === "unavailable" ? "Charger reported as unavailable" : "Ready for the next vehicle"}
        </p>
      </div>
    </div>
    <span className={`flex-shrink-0 text-xs font-semibold ${STATUS[status].text}`}>{STATUS[status].label}</span>
  </div>
);

const StationCard = ({ station, now, wide = false, className = "" }) => {
  const guns = station.guns.map((g) => ({ gun: g, status: statusOf(g) }));
  // the gun delivering the most power comes first (sort is stable, so ties keep their order)
  const sessions = guns
    .filter((g) => g.status !== "available" && g.status !== "unavailable")
    .sort((a, b) => gunPower(b.gun) - gunPower(a.gun));
  const idle = guns.filter((g) => g.status === "available" || g.status === "unavailable");
  const charging = guns.filter((g) => g.status === "charging").length;
  const live = guns.some((g) => isActive(g.status));
  const power = guns.reduce((a, g) => a + (g.status === "charging" ? g.gun.power_w || 0 : 0), 0);

  return (
    <article
      className={`relative flex flex-col overflow-hidden rounded-2xl border bg-white ${className} ${
        live
          ? "border-green-200 shadow-[0_8px_24px_-12px_rgb(22_101_52/0.25)]"
          : "border-neutral-200 shadow-card"
      }`}
    >
      {live && <div className="h-1 bg-gradient-to-r from-green-400 via-emerald-500 to-green-400" />}
      <header className={`flex items-start justify-between gap-3 px-5 pb-3 pt-4 ${live ? "bg-gradient-to-b from-green-50/70 to-white" : ""}`}>
        <div className="min-w-0">
          <Link
            to={`/charging-stations/${station.station_id}`}
            className="group inline-flex max-w-full items-center gap-1 text-[15px] font-semibold tracking-tight text-brand-950 hover:text-brand-700"
          >
            <span className="truncate">{station.station_name || station.station_id}</span>
            <ArrowUpRight className="h-3.5 w-3.5 flex-shrink-0 text-neutral-300 group-hover:text-brand-600" />
          </Link>
          <p className="mt-0.5 font-mono text-[11px] text-neutral-400">{station.station_id}</p>
        </div>
        {live ? (
          <div className="flex-shrink-0 text-right">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-600 px-2.5 py-1 text-[11px] font-semibold text-white">
              <StatusDot status="charging" size="h-1.5 w-1.5" />
              {charging > 0 ? `${charging} charging` : "Finishing"}
            </span>
            {power > 0 && (
              <p className="mt-1 text-xs font-semibold tabular-nums text-green-800">{formatDecimal(power / 1000)} kW</p>
            )}
          </div>
        ) : (
          <span className="flex-shrink-0 rounded-full bg-neutral-100 px-2.5 py-1 text-[11px] font-medium text-neutral-500">
            {guns.length} {guns.length === 1 ? "gun" : "guns"}
          </span>
        )}
      </header>

      <div className="flex flex-1 flex-col gap-3 px-5 pb-5">
        {sessions.length > 0 && (
          <div className={`grid gap-3 ${wide && sessions.length > 1 ? "md:grid-cols-2" : ""}`}>
            {sessions.map(({ gun, status }) => (
              <SessionGun key={gun.connector_id} gun={gun} status={status} now={now} />
            ))}
          </div>
        )}
        {idle.length > 0 && (
          <div className={`grid gap-2 ${wide && idle.length > 1 ? "md:grid-cols-2" : ""}`}>
            {idle.map(({ gun, status }) => (
              <IdleGun key={gun.connector_id} gun={gun} status={status} />
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

const Overview = ({ summary, isLoading }) => {
  const share = summary.total ? (summary.charging / summary.total) * 100 : 0;
  const cells = [
    { label: "Power being delivered", value: formatDecimal(summary.power), unit: "kW" },
    { label: "Energy in active sessions", value: formatDecimal(summary.energy), unit: "kWh" },
    { label: "Available", value: formatNumber(summary.available), unit: "guns" },
    { label: "Unavailable", value: formatNumber(summary.unavailable), unit: "guns", tone: summary.unavailable ? "text-red-700" : "" },
  ];
  return (
    <section className="grid overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-card lg:grid-cols-[minmax(260px,1.1fr)_2.4fr]">
      <div className="flex items-center gap-4 border-b border-neutral-200 bg-gradient-to-br from-green-50 via-white to-white p-5 lg:border-b-0 lg:border-r">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-green-600 text-white shadow-[0_6px_16px_-6px_rgb(22_163_74/0.6)]">
          <BatteryCharging className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wider text-green-800/80">Charging now</p>
          {isLoading ? (
            <Skeleton className="mt-1.5 h-8 w-24" />
          ) : (
            <p className="text-3xl font-semibold tracking-tight tabular-nums text-brand-950">
              {formatNumber(summary.charging)}
              <span className="ml-1.5 text-sm font-medium text-neutral-400">of {formatNumber(summary.total)} guns</span>
            </p>
          )}
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-green-100">
            <div className="h-full rounded-full bg-green-500 transition-[width] duration-700" style={{ width: `${share}%` }} />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 divide-neutral-200 sm:grid-cols-4 sm:divide-x">
        {cells.map((c) => (
          <div key={c.label} className="p-5">
            <p className="text-xs font-medium text-neutral-500">{c.label}</p>
            {isLoading ? (
              <Skeleton className="mt-2 h-7 w-16" />
            ) : (
              <p className={`mt-1 text-2xl font-semibold tracking-tight tabular-nums text-brand-950 ${c.tone || ""}`}>
                {c.value}
                <span className="ml-1 text-xs font-medium text-neutral-400">{c.unit}</span>
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const SectionHeading = ({ title, count, live }) => (
  <div className="flex items-center gap-3">
    {live && <StatusDot status="charging" />}
    <h2 className="text-sm font-semibold text-brand-950">{title}</h2>
    <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-neutral-500">{count}</span>
    <span className="h-px flex-1 bg-neutral-200" />
  </div>
);

const LiveCharging = () => {
  const [paused, setPaused] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const { stations, isLoading, error, unsupported, meterValues, updatedAt } = useLiveChargingStatus({ paused });

  // re-render every second so "elapsed" and "updated" stay current between polls
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const guns = useMemo(() => stations.flatMap((s) => s.guns.map((g) => ({ ...g, status: statusOf(g) }))), [stations]);
  const summary = useMemo(() => {
    const charging = guns.filter((g) => g.status === "charging");
    return {
      charging: charging.length,
      power: charging.reduce((a, g) => a + (g.power_w || 0), 0) / 1000,
      energy: guns.filter((g) => isActive(g.status)).reduce((a, g) => a + (g.energy_wh || 0), 0) / 1000,
      available: guns.filter((g) => g.status === "available").length,
      unavailable: guns.filter((g) => g.status === "unavailable").length,
      total: guns.length,
    };
  }, [guns]);

  // station counts shown on the filter buttons
  const filterOptions = useMemo(
    () =>
      FILTERS.map((f) => {
        const n = stations.filter((s) => s.guns.some((g) => matchesFilter(statusOf(g), f.value))).length;
        return { ...f, label: isLoading ? f.label : `${f.label} · ${n}` };
      }),
    [stations, isLoading]
  );

  const { active, rest } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rank = (s) => Math.min(...s.guns.map((g) => RANK.indexOf(statusOf(g))));
    const list = stations
      .filter((s) => !q || (s.station_name || "").toLowerCase().includes(q) || s.station_id.toLowerCase().includes(q))
      .filter((s) => s.guns.some((g) => matchesFilter(statusOf(g), filter)))
      .sort((a, b) => rank(a) - rank(b) || (a.station_name || a.station_id).localeCompare(b.station_name || b.station_id));
    const live = (s) => s.guns.some((g) => isActive(statusOf(g)));
    // charging stations: most power delivered first; ones charging at 0 kW go below them
    const byPower = (a, b) => stationPower(b) - stationPower(a);
    return { active: list.filter(live).sort(byPower), rest: list.filter((s) => !live(s)) };
  }, [stations, filter, search]);

  const empty = active.length === 0 && rest.length === 0;

  return (
    <div className="mx-auto max-w-[1600px] space-y-6 pb-10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-brand-950">Live Charging</h1>
          <p className="page-subtitle">Every charger, in real time. Stations that are charging appear first.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 items-center gap-2 rounded-md border border-neutral-200 bg-white px-3 text-xs font-medium text-neutral-600 shadow-card">
            <span className={`h-2 w-2 rounded-full ${paused ? "bg-neutral-400" : error ? "bg-red-500" : "animate-pulse bg-green-500"}`} />
            {paused ? "Paused" : updatedAt ? `Live · updated ${relative(updatedAt, now)}` : "Connecting…"}
          </span>
          <button type="button" className="btn-secondary" onClick={() => setPaused((p) => !p)}>
            {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
            {paused ? "Resume" : "Pause"}
          </button>
        </div>
      </div>

      {unsupported ? (
        <div className="card p-6 text-13 text-neutral-600">
          Live charging status isn't available on this server yet. It appears once the backend with
          <code className="mx-1 rounded bg-neutral-100 px-1 py-0.5 text-xs">cms_get_live_charging_status</code> is deployed.
        </div>
      ) : (
        <>
          <Overview summary={summary} isLoading={isLoading} />

          {!meterValues && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-13 text-amber-800">
              Voltage, current and power aren't connected on this server; status, energy and battery are still live.
            </p>
          )}
          {error && !isLoading && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-13 text-red-700">
              {stations.length ? "Showing the last update. " : ""}Couldn't refresh: {error}
            </p>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <SegmentedControl options={filterOptions} value={filter} onChange={setFilter} size="md" />
            <div className="relative sm:ml-auto sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search station name or ID"
                className="input w-full pl-9"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-64 w-full rounded-2xl" />)}
            </div>
          ) : empty ? (
            <div className="card p-10 text-center text-13 text-neutral-500">
              {stations.length ? "No chargers match this filter." : "No stations found for your group."}
            </div>
          ) : (
            <div className="space-y-8">
              {active.length > 0 && (
                <section className="space-y-4">
                  <SectionHeading title="Charging now" count={active.length} live />
                  <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
                    {active.map((s) => {
                      // two vehicles in session need the full row to stay readable
                      const wide = s.guns.filter((g) => !["available", "unavailable"].includes(statusOf(g))).length > 1;
                      return <StationCard key={s.station_id} station={s} now={now} wide={wide} className={wide ? "xl:col-span-2" : ""} />;
                    })}
                  </div>
                </section>
              )}
              {rest.length > 0 && (
                <section className="space-y-4">
                  <SectionHeading title={active.length ? "Other stations" : "Stations"} count={rest.length} />
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 min-[1800px]:grid-cols-4">
                    {rest.map((s) => <StationCard key={s.station_id} station={s} now={now} />)}
                  </div>
                </section>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LiveCharging;
